import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ExerciseForm = ({ 
  exercise = null, 
  onSave, 
  onCancel, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    name: exercise?.name || '',
    description: exercise?.description || '',
    category: exercise?.category || 'strength',
    type: exercise?.type || 'dynamic',
    image: exercise?.image || '',
    hasVideo: exercise?.hasVideo || false,
    videoUrl: exercise?.videoUrl || '',
    difficultyLevels: exercise?.difficultyLevels || [
      { phase: 'beginner', reps: '2-4', load: 'Light' },
      { phase: 'intermediate', reps: '6-8', load: 'Moderate' },
      { phase: 'advanced', reps: '10-12', load: 'Heavy' }
    ],
    isActive: exercise?.isActive ?? true,
    instructions: exercise?.instructions || '',
    equipment: exercise?.equipment || '',
    targetMuscles: exercise?.targetMuscles || '',
    precautions: exercise?.precautions || ''
  });

  const [imagePreview, setImagePreview] = useState(exercise?.image || '');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const categories = [
    { value: 'strength', label: 'Strength Training' },
    { value: 'cardio', label: 'Cardiovascular' },
    { value: 'flexibility', label: 'Flexibility' },
    { value: 'balance', label: 'Balance' },
    { value: 'rehabilitation', label: 'Rehabilitation' },
    { value: 'mobility', label: 'Mobility' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDifficultyChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      difficultyLevels: prev?.difficultyLevels?.map((level, i) => 
        i === index ? { ...level, [field]: value } : level
      )
    }));
  };

  const handleImageUpload = (file) => {
    if (file && file?.type?.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e?.target?.result;
        setImagePreview(imageUrl);
        handleInputChange('image', imageUrl);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragActive(false);
    const file = e?.dataTransfer?.files?.[0];
    handleImageUpload(file);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setDragActive(false);
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    handleImageUpload(file);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(formData);
  };

  const isEditing = !!exercise;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl border border-border max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">
              {isEditing ? 'Edit Exercise' : 'Add New Exercise'}
            </h2>
            <p className="text-muted-foreground">
              {isEditing ? 'Update exercise details and settings' : 'Create a new exercise for the database'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            className="breathing-focus"
          >
            <Icon name="X" size={24} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Input
                label="Exercise Name"
                type="text"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                placeholder="Enter exercise name"
                required
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Category
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {categories?.map((category) => (
                    <Button
                      key={category?.value}
                      type="button"
                      variant={formData?.category === category?.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleInputChange('category', category?.value)}
                      className="breathing-focus"
                    >
                      {category?.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Exercise Type
                </label>
                <div className="flex space-x-2">
                  <Button
                    type="button"
                    variant={formData?.type === 'dynamic' ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleInputChange('type', 'dynamic')}
                    className="breathing-focus flex-1"
                  >
                    <Icon name="Zap" size={16} className="mr-2" />
                    Dynamic
                  </Button>
                  <Button
                    type="button"
                    variant={formData?.type === 'static' ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleInputChange('type', 'static')}
                    className="breathing-focus flex-1"
                  >
                    <Icon name="Pause" size={16} className="mr-2" />
                    Static
                  </Button>
                </div>
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Exercise Image
              </label>
              <div
                className={`
                  border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200
                  ${dragActive 
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }
                `}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                {imagePreview ? (
                  <div className="space-y-4">
                    <img
                      src={imagePreview}
                      alt="Exercise preview"
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef?.current?.click()}
                      className="breathing-focus"
                    >
                      <Icon name="Upload" size={16} className="mr-2" />
                      Change Image
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Icon name="Upload" size={48} className="text-muted-foreground mx-auto" />
                    <div>
                      <p className="text-foreground font-medium">
                        Drop image here or click to upload
                      </p>
                      <p className="text-sm text-muted-foreground">
                        PNG, JPG up to 10MB
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef?.current?.click()}
                      className="breathing-focus"
                    >
                      Select Image
                    </Button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              placeholder="Describe the exercise and its benefits"
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Difficulty Levels */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Difficulty Levels</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {formData?.difficultyLevels?.map((level, index) => (
                <div key={index} className="bg-muted/30 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-foreground capitalize">{level?.phase}</h4>
                  <Input
                    label="Repetitions"
                    type="text"
                    value={level?.reps}
                    onChange={(e) => handleDifficultyChange(index, 'reps', e?.target?.value)}
                    placeholder="e.g., 8-12"
                  />
                  <Input
                    label="Load Level"
                    type="text"
                    value={level?.load}
                    onChange={(e) => handleDifficultyChange(index, 'load', e?.target?.value)}
                    placeholder="e.g., Light, Moderate"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Additional Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Equipment Required"
              type="text"
              value={formData?.equipment}
              onChange={(e) => handleInputChange('equipment', e?.target?.value)}
              placeholder="e.g., Dumbbells, Resistance bands"
            />

            <Input
              label="Target Muscles"
              type="text"
              value={formData?.targetMuscles}
              onChange={(e) => handleInputChange('targetMuscles', e?.target?.value)}
              placeholder="e.g., Quadriceps, Hamstrings"
            />
          </div>

          {/* Instructions and Precautions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Instructions
              </label>
              <textarea
                value={formData?.instructions}
                onChange={(e) => handleInputChange('instructions', e?.target?.value)}
                placeholder="Step-by-step exercise instructions"
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Precautions
              </label>
              <textarea
                value={formData?.precautions}
                onChange={(e) => handleInputChange('precautions', e?.target?.value)}
                placeholder="Safety considerations and contraindications"
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-between pt-6 border-t border-border">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData?.isActive}
                onChange={(e) => handleInputChange('isActive', e?.target?.checked)}
                className="rounded border-border"
              />
              <label htmlFor="isActive" className="text-sm text-foreground">
                Exercise is active and available for use
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isLoading}
                className="breathing-focus"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isLoading}
                className="breathing-focus"
              >
                {isEditing ? 'Update Exercise' : 'Create Exercise'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExerciseForm;